import { useState, useEffect } from 'react';
import { firmendatenApi } from '../api/firmendatenApi';

// Globaler Cache für Firmendaten - mit OrganisationId
let cachedFirmendaten = null;
let cachedOrganisationId = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

// Event-Name für Updates
const FIRMENDATEN_UPDATE_EVENT = 'firmendaten-updated';
const AUTH_CHANGE_EVENT = 'auth-changed';

// Hilfsfunktion um OrganisationId aus Token zu extrahieren
const getOrganisationIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.organisationId || null;
  } catch {
    return null;
  }
};

export function useFirmendaten() {
  const [firmendaten, setFirmendaten] = useState(cachedFirmendaten);
  const [loading, setLoading] = useState(!cachedFirmendaten);

  useEffect(() => {
    const loadFirmendaten = async () => {
      const now = Date.now();
      const currentOrgId = getOrganisationIdFromToken();

      // Cache nur nutzen wenn:
      // 1. Cache existiert
      // 2. Cache nicht abgelaufen ist
      // 3. Organisation die gleiche ist
      if (cachedFirmendaten &&
          (now - cacheTimestamp) < CACHE_DURATION &&
          cachedOrganisationId === currentOrgId) {
        setFirmendaten(cachedFirmendaten);
        setLoading(false);
        return;
      }

      try {
        // Wenn eingeloggt, authentifizierte Route nutzen
        const token = localStorage.getItem('token');
        const data = token
          ? await firmendatenApi.get()
          : await firmendatenApi.getPublic();

        cachedFirmendaten = data;
        cachedOrganisationId = currentOrgId;
        cacheTimestamp = now;
        setFirmendaten(data);
      } catch (error) {
        console.error('Fehler beim Laden der Firmendaten:', error);
        // Fallback - leerer Firmenname, damit "WerkPilot" ohne Prefix angezeigt wird
        setFirmendaten({
          firmenname: '',
          telefon: '',
          email: ''
        });
      } finally {
        setLoading(false);
      }
    };

    loadFirmendaten();

    // Event-Listener für Updates
    const handleUpdate = () => {
      cachedFirmendaten = null;
      cachedOrganisationId = null;
      cacheTimestamp = 0;
      loadFirmendaten();
    };

    // Auth-Änderungen überwachen (Login/Logout)
    const handleAuthChange = () => {
      cachedFirmendaten = null;
      cachedOrganisationId = null;
      cacheTimestamp = 0;
      loadFirmendaten();
    };

    window.addEventListener(FIRMENDATEN_UPDATE_EVENT, handleUpdate);
    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    window.addEventListener('storage', (e) => {
      if (e.key === 'token') {
        handleAuthChange();
      }
    });

    return () => {
      window.removeEventListener(FIRMENDATEN_UPDATE_EVENT, handleUpdate);
      window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    };
  }, []);

  return { firmendaten, loading };
}

// Helper-Funktion zum Triggern eines Updates
export function triggerFirmendatenUpdate() {
  cachedFirmendaten = null;
  cachedOrganisationId = null;
  cacheTimestamp = 0;
  window.dispatchEvent(new CustomEvent(FIRMENDATEN_UPDATE_EVENT));
}

// Helper-Funktion für Auth-Änderungen (wird von AuthContext aufgerufen)
export function triggerAuthChange() {
  cachedFirmendaten = null;
  cachedOrganisationId = null;
  cacheTimestamp = 0;
  window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT));
}
